/**
 * ChannelConnections — connect, review and disconnect ad-channel accounts.
 *
 * The OAuth round trip lands back on this page with a `?connect=` result, so
 * the component owns three things: the connected list, the outcome banner, and
 * the account picker.
 *
 * WHY THE PICKER EXISTS. A Google grant frequently reaches several accounts
 * (a manager plus its clients). The server cannot guess which one to operate,
 * and the callback used to fail with `multiple_accounts` — leaving the user to
 * supply a 10-digit customer id that nothing in the product ever showed them.
 * The callback now returns the candidates, and this renders them by name.
 * Choosing costs one more consent because the first grant's refresh token is
 * deliberately discarded rather than parked somewhere while the user decides.
 */

import { useMemo, useState } from 'react';
import {
  useAdoptAccounts,
  useAdoptOAuthInit,
  useAdoptDisconnectAccount,
} from '../hooks/useAdvertisingOptimization';
import type { AdoptChannelAccountDto } from '@/lib/api';

interface Props {
  holderId: string;
}

interface AccountChoice {
  id: string;
  name: string | null;
}

/** Human copy for every `reason` the OAuth callback can redirect with. */
const CONNECT_ERRORS: Record<string, string> = {
  no_operable_account:
    'That Google account cannot operate any ad accounts. Sign in as the user who manages the Google Ads account you want to connect.',
  target_account_not_accessible:
    'The chosen account is not reachable by that Google sign-in. Pick another, or sign in as a user with access to it.',
  invalid_state: 'The connection attempt expired. Connections must be completed within 15 minutes.',
  invalid_issuer: 'The response did not come from Google and was rejected.',
  missing_params: 'Google returned an incomplete response. Please try again.',
  access_denied: 'You declined the permission request.',
  internal: 'Something went wrong completing the connection. Please try again.',
};

function readConnectResult(): {
  status: string | null;
  reason: string | null;
  choices: AccountChoice[];
} {
  if (typeof window === 'undefined') return { status: null, reason: null, choices: [] };
  const params = new URLSearchParams(window.location.search);
  const status = params.get('connect');
  const reason = params.get('reason');
  let choices: AccountChoice[] = [];
  const raw = params.get('accounts');
  if (raw) {
    try {
      const decoded = JSON.parse(atob(raw.replace(/-/g, '+').replace(/_/g, '/'))) as unknown;
      // Server-generated, but parsed defensively and rendered as TEXT only.
      if (Array.isArray(decoded)) {
        choices = decoded
          .filter((c): c is AccountChoice => !!c && typeof (c as AccountChoice).id === 'string')
          .slice(0, 10)
          .map((c) => ({ id: c.id, name: typeof c.name === 'string' ? c.name : null }));
      }
    } catch {
      choices = [];
    }
  }
  return { status, reason, choices };
}

/** Drops the `?connect=…` params so a reload does not replay the banner. */
function clearConnectParams(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  ['connect', 'reason', 'accounts'].forEach((k) => url.searchParams.delete(k));
  window.history.replaceState({}, '', url.toString());
}

function formatCustomerId(id: string | null): string {
  if (!id) return '—';
  const d = id.replace(/\D/g, '');
  return d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : id;
}

function AccountRow({
  account,
  onDisconnect,
  disconnecting,
}: {
  account: AdoptChannelAccountDto;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  const active = account.status === 'active';
  return (
    <li className="flex items-center justify-between gap-4 rounded-md border border-border px-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">
            {account.descriptiveName ?? formatCustomerId(account.externalCustomerId)}
          </span>
          <span
            className={`text-xs px-1.5 py-0.5 rounded ${
              active ? 'bg-emerald-500/10 text-emerald-600' : 'bg-amber-500/10 text-amber-600'
            }`}
          >
            {account.status}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          Google Ads · {formatCustomerId(account.externalCustomerId)} · {account.mode.toUpperCase()}
          {account.currencyCode ? ` · ${account.currencyCode}` : ''}
        </div>
        {account.lastError && (
          <div className="text-xs text-rose-600 mt-1">Last error: {account.lastError}</div>
        )}
      </div>
      <button
        onClick={onDisconnect}
        disabled={disconnecting}
        className="shrink-0 px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        {disconnecting ? 'Disconnecting…' : 'Disconnect'}
      </button>
    </li>
  );
}

export function ChannelConnections({ holderId }: Props) {
  const accountsQuery = useAdoptAccounts(holderId);
  const oauthInit = useAdoptOAuthInit(holderId);
  const disconnect = useAdoptDisconnectAccount(holderId);

  // Read once on mount: the params describe the round trip we just returned
  // from, not live state.
  const initial = useMemo(readConnectResult, []);
  const [banner, setBanner] = useState(initial);
  const [pendingChoice, setPendingChoice] = useState<string | null>(initial.choices[0]?.id ?? null);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState<string | null>(null);

  const connect = (targetCustomerId?: string): void => {
    oauthInit.mutate(targetCustomerId, {
      onSuccess: (res) => {
        clearConnectParams();
        window.location.assign(res.authorizationUrl);
      },
    });
  };

  const dismiss = (): void => {
    clearConnectParams();
    setBanner({ status: null, reason: null, choices: [] });
  };

  const accounts = accountsQuery.data ?? [];

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium">Ad channel accounts</h2>
          <p className="text-xs text-muted-foreground">
            Campaigns run in a Google Ads account you connect here.
          </p>
        </div>
        {accounts.length > 0 && (
          <button
            onClick={() => connect()}
            disabled={oauthInit.isPending}
            className="px-3 py-1.5 rounded-md border border-border text-sm disabled:opacity-50"
          >
            {oauthInit.isPending ? 'Starting…' : 'Connect another'}
          </button>
        )}
      </div>

      {banner.status === 'success' && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm">
          <span>Google Ads account connected.</span>
          <button onClick={dismiss} className="text-xs text-muted-foreground hover:text-foreground">
            Dismiss
          </button>
        </div>
      )}

      {banner.status === 'error' && (
        <div className="flex items-start justify-between gap-3 rounded-md border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-sm">
          <span>
            {CONNECT_ERRORS[banner.reason ?? ''] ??
              `The connection did not complete (${banner.reason ?? 'unknown'}).`}
          </span>
          <button onClick={dismiss} className="text-xs text-muted-foreground hover:text-foreground">
            Dismiss
          </button>
        </div>
      )}

      {banner.status === 'choose' && banner.choices.length > 0 && (
        <div className="rounded-md border border-border px-4 py-3 flex flex-col gap-3">
          <div>
            <div className="text-sm font-medium">Which account should Fias use?</div>
            <p className="text-xs text-muted-foreground">
              That Google sign-in can reach several ad accounts. Choosing asks Google to confirm
              once more — Fias does not hold on to the first approval.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            {banner.choices.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="adopt-account-choice"
                  value={c.id}
                  checked={pendingChoice === c.id}
                  onChange={() => setPendingChoice(c.id)}
                />
                <span className="truncate">{c.name ?? 'Unnamed account'}</span>
                <span className="text-xs text-muted-foreground">{formatCustomerId(c.id)}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => pendingChoice && connect(pendingChoice)}
              disabled={!pendingChoice || oauthInit.isPending}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              {oauthInit.isPending ? 'Starting…' : 'Continue with this account'}
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-1.5 rounded-md border border-border text-sm text-muted-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {oauthInit.isError && (
        <div className="text-sm text-rose-600">
          Could not start the connection.{' '}
          {oauthInit.error instanceof Error && oauthInit.error.message.includes('503')
            ? 'Google Ads is not configured on this server.'
            : 'Please try again.'}
        </div>
      )}

      {accountsQuery.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading accounts…</div>
      ) : accounts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 flex flex-col items-start gap-3">
          <div>
            <div className="text-sm font-medium">No ad account connected</div>
            <p className="text-sm text-muted-foreground">
              Connect a Google Ads account to deploy campaigns. Fias never takes a campaign live on
              its own — deploying always leaves it paused for you to start.
            </p>
          </div>
          <button
            onClick={() => connect()}
            disabled={oauthInit.isPending}
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
          >
            {oauthInit.isPending ? 'Starting…' : 'Connect Google Ads'}
          </button>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {accounts.map((a) => (
            <AccountRow
              key={a.channelAccountId}
              account={a}
              disconnecting={disconnect.isPending && confirmingDisconnect === a.channelAccountId}
              onDisconnect={() => {
                // Disconnect erases the stored tokens and any placement using
                // the account stops deploying — worth one confirmation.
                if (confirmingDisconnect !== a.channelAccountId) {
                  setConfirmingDisconnect(a.channelAccountId);
                  return;
                }
                disconnect.mutate(a.channelAccountId, {
                  onSettled: () => setConfirmingDisconnect(null),
                });
              }}
            />
          ))}
        </ul>
      )}

      {confirmingDisconnect && (
        <div className="text-xs text-muted-foreground">
          Click Disconnect again to confirm. Campaigns already deployed keep running in Google Ads;
          Fias just stops managing them.
        </div>
      )}

      {disconnect.isError && (
        <div className="text-sm text-rose-600">Could not disconnect. Please try again.</div>
      )}
    </section>
  );
}
